import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  Building2, Plus, X, ArrowUpCircle, ArrowDownCircle, RefreshCw,
  FileText, Eye, Edit2, Trash2, ArrowRightLeft, DollarSign,
  TrendingUp, TrendingDown, CheckCircle, Clock, Upload, Paperclip,
  Sparkles, Zap, Link2, AlertCircle, AlertTriangle, Activity, BookOpen
} from 'lucide-react';
import { useFocusEntity } from '../hooks/useFocusEntity';
import ReconciliationVersionsPanel from '../components/ReconciliationVersionsPanel';
import MatchReasonCard from '../components/MatchReasonCard';
import BankStatementsTab from '../components/BankStatementsTab';
import OrphanReport from '../components/OrphanReport';
import DiagnoseRepairResultModal from '../components/DiagnoseRepairResultModal';

const Bank = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { focusedId, registerRow } = useFocusEntity();
  const [activeTab, setActiveTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState([]);   // bank health drift cards
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('account');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [matchReason, setMatchReason] = useState(null);
  const [detailType, setDetailType] = useState('account');
  const [uploading, setUploading] = useState(false);

  const isAdmin = user?.role === 'admin';

  const [accountForm, setAccountForm] = useState({
    bank_name: '',
    bank_name_arabic: '',
    account_name: '',
    account_number: '',
    iban: '',
    swift_code: '',
    branch: '',
    currency: 'SAR',
    opening_balance: 0,
    notes: '',
    attachment_url: ''
  });

  const [transactionForm, setTransactionForm] = useState({
    bank_account_id: '',
    transaction_type: 'deposit',
    amount: 0,
    transaction_date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    payee_payer: '',
    transfer_to_account_id: '',
    category: '',
    attachment_url: ''
  });

  const [reconciliationForm, setReconciliationForm] = useState({
    bank_account_id: '',
    statement_date: new Date().toISOString().split('T')[0],
    statement_balance: 0,
    notes: ''
  });

  // ---- P4 Bank Realtime Engine ----
  const [engineSummary, setEngineSummary] = useState(null);
  const [engineLinks, setEngineLinks] = useState([]);
  const [bankRules, setBankRules] = useState([]);
  const [engineFilter, setEngineFilter] = useState('all');
  const [engineRunning, setEngineRunning] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    rule_name: '',
    keywords: '',
    account_name: '',
    confidence_threshold: 0.8,
    notes: ''
  });

  // ---- P4.1 Bank AI Engine ----
  const [aiSummary, setAiSummary] = useState(null);
  const [aiMatches, setAiMatches] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [aiView, setAiView] = useState('overview'); // overview | review | matches

  const fetchEngineData = useCallback(async () => {
    try {
      const [s, l, r, ai, q, m] = await Promise.all([
        axiosInstance.get('/bank/engine/dashboard'),
        axiosInstance.get('/bank/engine/links?limit=200'),
        axiosInstance.get('/bank/rules'),
        axiosInstance.get('/bank/ai/dashboard'),
        axiosInstance.get('/bank/ai/review-queue'),
        axiosInstance.get('/bank/ai/matches?limit=200')
      ]);
      setEngineSummary(s.data);
      setEngineLinks(l.data);
      setBankRules(r.data);
      setAiSummary(ai.data);
      setReviewQueue(q.data);
      setAiMatches(m.data);
    } catch (e) {
      console.error('Engine fetch failed', e);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'engine') fetchEngineData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Phase 7.x · Bank → Journal Navigation (incoming side).
  // When the URL carries `?focus=<tx_id>` (because the user clicked
  // a bank event in the Audit Stream Widget or hit a reverse link from
  // the Journal page), jump straight to the Transactions tab so the
  // focused row is actually visible before the pulse fires.
  useEffect(() => {
    if (focusedId && activeTab !== 'transactions') {
      setActiveTab('transactions');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId]);

  const handleRerunEngine = async () => {
    try {
      setEngineRunning(true);
      const r = await axiosInstance.post('/bank/engine/rerun?only_unmatched=true');
      toast.success(`تمت معالجة ${r.data.processed} وربط ${r.data.matched} / Processed ${r.data.processed}, matched ${r.data.matched}`);
      await fetchEngineData();
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('bank.engine_rerun_failed'));
    } finally {
      setEngineRunning(false);
    }
  };

  const handleUnlink = async (transactionId) => {
    if (!window.confirm(t('bank.unlink_this_match'))) return;
    try {
      await axiosInstance.delete(`/bank/engine/links/${transactionId}`);
      toast.success(t('bank.unlinked'));
      await fetchEngineData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openRuleModal = (rule = null) => {
    if (rule) {
      setSelectedRule(rule);
      setRuleForm({
        rule_name: rule.rule_name || '',
        keywords: (rule.keywords || []).join(', '),
        account_name: rule.account_name || '',
        confidence_threshold: rule.confidence_threshold || 0.8,
        notes: rule.notes || ''
      });
    } else {
      setSelectedRule(null);
      setRuleForm({ rule_name: '', keywords: '', account_name: '', confidence_threshold: 0.8, notes: '' });
    }
    setShowRuleModal(true);
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    const payload = {
      rule_name: ruleForm.rule_name,
      keywords: ruleForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      account_name: ruleForm.account_name,
      confidence_threshold: parseFloat(ruleForm.confidence_threshold) || 0.8,
      notes: ruleForm.notes
    };
    try {
      if (selectedRule) {
        await axiosInstance.put(`/bank/rules/${selectedRule.id}`, payload);
        toast.success(t('bank.rule_updated'));
      } else {
        await axiosInstance.post('/bank/rules', payload);
        toast.success(t('bank.rule_added'));
      }
      setShowRuleModal(false);
      await fetchEngineData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm(t('bank.delete_rule'))) return;
    try {
      await axiosInstance.delete(`/bank/rules/${id}`);
      toast.success(t('bank.deleted'));
      await fetchEngineData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // ---- AI review actions ----
  const handleConfirmReview = async (txId) => {
    try {
      await axiosInstance.post(`/bank/ai/review/${txId}/confirm`);
      toast.success(t('bank.match_confirmed'));
      await fetchEngineData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleRejectReview = async (txId) => {
    try {
      await axiosInstance.post(`/bank/ai/review/${txId}/reject`);
      toast.success(t('bank.rejected'));
      await fetchEngineData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [accountsRes, transactionsRes, reconciliationsRes, summaryRes, healthRes] = await Promise.all([
        axiosInstance.get('/bank/accounts'),
        axiosInstance.get('/bank/transactions'),
        axiosInstance.get('/bank/reconciliation'),
        axiosInstance.get('/bank/reports/summary'),
        axiosInstance.get('/bank/health').catch(() => ({ data: { banks: [] } })),
      ]);
      setAccounts(accountsRes.data);
      setTransactions(transactionsRes.data);
      setReconciliations(reconciliationsRes.data);
      setSummary(summaryRes.data);
      setHealth(healthRes.data?.banks || []);
    } catch (error) {
      console.error('Error fetching bank data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e, formType) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('bank.please_select_pdf_or_image_only'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axiosInstance.post('/bank/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (formType === 'account') {
        setAccountForm({ ...accountForm, attachment_url: response.data.url });
      } else if (formType === 'transaction') {
        setTransactionForm({ ...transactionForm, attachment_url: response.data.url });
      }
      
      toast.success(t('bank.file_uploaded_successfully'));
    } catch (error) {
      toast.error(t('bank.failed_to_upload_file'));
    } finally {
      setUploading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      if (selectedAccount) {
        // Update existing account
        await axiosInstance.put(`/bank/accounts/${selectedAccount.id}`, accountForm);
        toast.success(t('bank.bank_account_updated'));
      } else {
        // Create new account
        await axiosInstance.post('/bank/accounts', accountForm);
        toast.success(t('bank.bank_account_created'));
      }
      setShowModal(false);
      resetAccountForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_save_account'));
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      if (selectedTransaction) {
        // Update existing transaction
        await axiosInstance.put(`/bank/transactions/${selectedTransaction.id}`, {
          ...transactionForm,
          amount: parseFloat(transactionForm.amount)
        });
        toast.success(t('bank.transaction_updated'));
      } else {
        // Create new transaction — handle B3 self-healing guardrail.
        // Backend returns HTTP 409 with `code: manual_bank_tx_guardrail`
        // when the chosen transaction_type belongs to a different flow
        // (e.g. bill_payment → Supplier Payment screen). Show the
        // operator the suggestion + give them an explicit option to
        // proceed anyway (e.g. for legacy data backfill).
        const payload = {
          ...transactionForm,
          amount: parseFloat(transactionForm.amount),
        };
        try {
          await axiosInstance.post('/bank/transactions', payload);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (
            err?.response?.status === 409 &&
            detail && typeof detail === 'object' &&
            detail.code === 'manual_bank_tx_guardrail'
          ) {
            const proceed = window.confirm(
              `${detail.message}\n\n${detail.hint}\n\nهل تَريد المُتابَعة على أيّ حال (Legacy Backfill)؟`,
            );
            if (!proceed) {
              return; // operator chose the right route — abort.
            }
            // Operator confirmed → re-post with confirm_orphan=true.
            await axiosInstance.post(
              '/bank/transactions?confirm_orphan=true',
              payload,
            );
          } else {
            throw err;
          }
        }
        toast.success(t('bank.transaction_recorded'));
      }
      setShowModal(false);
      resetTransactionForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_save_transaction'));
    }
  };

  const handleCreateReconciliation = async (e) => {
    e.preventDefault();
    try {
      if (selectedReconciliation) {
        // Update existing reconciliation
        await axiosInstance.put(`/bank/reconciliation/${selectedReconciliation.id}`, {
          ...reconciliationForm,
          statement_balance: parseFloat(reconciliationForm.statement_balance)
        });
        toast.success(t('bank.reconciliation_updated'));
      } else {
        // Create new reconciliation
        await axiosInstance.post('/bank/reconciliation', {
          ...reconciliationForm,
          statement_balance: parseFloat(reconciliationForm.statement_balance)
        });
        toast.success(t('bank.reconciliation_started'));
      }
      setShowModal(false);
      resetReconciliationForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_start_reconciliation'));
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm(t('bank.are_you_sure_you_want_to_delete_this_acc'))) return;
    try {
      await axiosInstance.delete(`/bank/accounts/${id}?force=true`);
      toast.success(t('bank.account_deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_delete_account'));
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm(t('bank.are_you_sure_you_want_to_delete_this_tra'))) return;
    try {
      await axiosInstance.delete(`/bank/transactions/${id}`);
      toast.success(t('bank.transaction_deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_delete_transaction'));
    }
  };

  const handleDeleteReconciliation = async (id) => {
    if (!window.confirm(t('bank.are_you_sure_you_want_to_delete_this_rec'))) return;
    try {
      await axiosInstance.delete(`/bank/reconciliation/${id}`);
      toast.success(t('bank.reconciliation_deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_delete_reconciliation'));
    }
  };

  const [recalculating, setRecalculating] = useState(false);
  const [healingOrphans, setHealingOrphans] = useState(false);
  const [dedupingTx, setDedupingTx] = useState(false);

  // Self-Healing — single-button Diagnose & Repair (Feb 2026).
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnoseReport, setDiagnoseReport] = useState(null);

  const runDiagnoseAndRepair = async () => {
    if (!window.confirm(
      'سَيَتِم تَشغيل التَّشخيص والإصلاح التِلقائي الشامِل:\n' +
      '  1) تَحديث الأرصدة المُتَتالية\n' +
      '  2) Backfill لمرايا Bank Adjustments\n' +
      '  3) تَنظيف يَتامى Smart Engine\n' +
      '  4) فَحص السِجِلَّات اليَتيمة\n' +
      '  5) مُقارَنة Stored vs Σ Tx vs GL\n\nهل تُريد المُتابَعة؟'
    )) return;
    setDiagnosing(true);
    setDiagnoseReport(null);
    try {
      const { data } = await axiosInstance.post('/bank/health/diagnose-and-repair');
      setDiagnoseReport(data);
      if (data.all_green) {
        toast.success('✅ النِظام سَليم — لا مَشاكِل');
      } else if (data.fixed.length > 0 && data.needs_manual.length === 0) {
        toast.success(`✅ تَم إِصلاح ${data.fixed.length} مَشاكِل تِلقائياً`);
      } else {
        toast.warning(
          `تَم الإصلاح: ${data.fixed.length} · يَحتاج تَدَخُّل: ${data.needs_manual.length}`,
        );
      }
      // Refresh the page's bank-health panel + balances + transactions.
      try {
        await fetchData();
      } catch (_e) {
        // best-effort refresh — the modal already has the data we need.
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل التَّشخيص');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleHealOrphans = async () => {
    // Two-step: dry-run first to preview, then execute after confirm.
    setHealingOrphans(true);
    try {
      const { data } = await axiosInstance.post(
        '/bank/heal-orphan-transactions?dry_run=true');
      if (data.orphan_count === 0) {
        toast.success('لا توجد حركات يتيمة — كل شيء متصل بشكل صحيح ✓');
        return;
      }
      const preview = (data.healed || []).slice(0, 3).map(h =>
        `• ${h.transaction_no} (${h.amount}) → ${h.new_bank_account_name}`
      ).join('\n');
      const ambiguousNote = data.ambiguous_count
        ? `\n${data.ambiguous_count} حركة غامضة تحتاج تدخلاً يدوياً.`
        : '';
      const msg = `${data.orphan_count} حركة يتيمة (${data.healed_count} قابلة للإصلاح تلقائياً).${ambiguousNote}\n\n${preview}\n\nهل تريد التنفيذ؟`;
      if (!window.confirm(msg)) return;
      const { data: exec } = await axiosInstance.post(
        '/bank/heal-orphan-transactions?dry_run=false');
      toast.success(`تم علاج ${exec.healed_count} حركة يتيمة ✓ — الآن اضغط «إعادة حساب الأرصدة»`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل علاج الحركات');
    } finally {
      setHealingOrphans(false);
    }
  };

  const handleDedupeTransactions = async () => {
    setDedupingTx(true);
    try {
      const { data } = await axiosInstance.post(
        '/bank/dedupe-transactions?dry_run=true');
      if (data.duplicate_groups === 0) {
        toast.success('لا توجد حركات مكرَّرة — كل الحركات فريدة ✓');
        return;
      }
      const preview = (data.duplicates || []).slice(0, 3).map(d =>
        `• ${d.date} · ${d.amount} SAR · نبقي ${d.kept} · نزيل ${d.removed.join(', ')}`
      ).join('\n');
      const msg = `${data.duplicate_groups} مجموعة مكرَّرة (${data.removed_count} حركة ستُحذف):\n\n${preview}\n\nهل تريد التنفيذ؟ (يمكن استرجاع المحذوفات لأنها soft-delete)`;
      if (!window.confirm(msg)) return;
      const { data: exec } = await axiosInstance.post(
        '/bank/dedupe-transactions?dry_run=false');
      toast.success(`تم حذف ${exec.removed_count} حركة مكرَّرة ✓ — الآن اضغط «إعادة حساب الأرصدة»`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل حذف المكرَّرات');
    } finally {
      setDedupingTx(false);
    }
  };



  const handleRecalculateBalances = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة حساب جميع الأرصدة؟\nAre you sure you want to recalculate all balances?')) {
      return;
    }
    try {
      setRecalculating(true);
      const response = await axiosInstance.post('/bank/recalculate-balances');
      const { total_fixed, fixed_accounts = [] } = response.data;
      if (total_fixed > 0) {
        toast.success(`تم إصلاح ${total_fixed} حساب/حسابات\n${total_fixed} account(s) fixed`);
        // Detailed breakdown so the accountant can audit what shifted.
        fixed_accounts.slice(0, 5).forEach((a) => {
          const diffFmt = (a.diff >= 0 ? '+' : '') + Number(a.diff).toLocaleString();
          toast.info(
            `${a.bank_name} · قديم ${Number(a.cached_balance).toLocaleString()} → جديد ${Number(a.computed_balance).toLocaleString()} (${diffFmt})`,
            { duration: 8000 });
        });
      } else {
        toast.success('جميع الأرصدة صحيحة\nAll balances are correct');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bank.failed_to_recalculate_balances'));
    } finally {
      setRecalculating(false);
    }
  };

  const resetAccountForm = () => {
    setAccountForm({
      bank_name: '', bank_name_arabic: '', account_name: '', account_number: '',
      iban: '', swift_code: '', branch: '', currency: 'SAR', opening_balance: 0, notes: '', attachment_url: ''
    });
    setSelectedAccount(null);
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      bank_account_id: '', transaction_type: 'deposit', amount: 0,
      transaction_date: new Date().toISOString().split('T')[0],
      reference: '', description: '', payee_payer: '', transfer_to_account_id: '', category: '', attachment_url: ''
    });
    setSelectedTransaction(null);
  };

  const resetReconciliationForm = () => {
    setReconciliationForm({
      bank_account_id: '', statement_date: new Date().toISOString().split('T')[0],
      statement_balance: 0, notes: ''
    });
    setSelectedReconciliation(null);
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    if (type === 'account' && item) {
      setAccountForm(item);
      setSelectedAccount(item);
    } else if (type === 'transaction' && item) {
      setTransactionForm({
        bank_account_id: item.bank_account_id || '',
        transaction_type: item.transaction_type || 'deposit',
        amount: item.amount || 0,
        transaction_date: item.transaction_date || new Date().toISOString().split('T')[0],
        reference: item.reference || '',
        description: item.description || '',
        payee_payer: item.payee_payer || '',
        transfer_to_account_id: item.transfer_to_account_id || '',
        category: item.category || '',
        attachment_url: item.attachment_url || ''
      });
      setSelectedTransaction(item);
    } else if (type === 'reconciliation' && item) {
      setReconciliationForm({
        bank_account_id: item.bank_account_id || '',
        statement_date: item.statement_date || new Date().toISOString().split('T')[0],
        statement_balance: item.statement_balance || 0,
        notes: item.notes || ''
      });
      setSelectedReconciliation(item);
    }
    setShowModal(true);
  };

  const openDetailModal = (type, item) => {
    setDetailType(type);
    if (type === 'account') {
      setSelectedAccount(item);
    } else if (type === 'transaction') {
      setSelectedTransaction(item);
      // Phase 7.x · Fix #3 — Explainable Matching.
      // The match_reason is the persisted Reason Object. We refresh
      // from the explain endpoint so the UI always shows the latest
      // reason even if the tx was matched after the page first loaded.
      setMatchReason(item.match_reason || null);
      if (item.id) {
        axiosInstance
          .get(`/match-explain/transaction/${item.id}`)
          .then((res) => setMatchReason(res.data?.reason || null))
          .catch(() => {});
      }
    } else if (type === 'reconciliation') {
      setSelectedReconciliation(item);
    }
    setShowDetailModal(true);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownCircle className="w-5 h-5 text-green-500" />;
      case 'withdrawal': return <ArrowUpCircle className="w-5 h-5 text-red-500" />;
      case 'transfer_in': return <ArrowRightLeft className="w-5 h-5 text-blue-500" />;
      case 'transfer_out': return <ArrowRightLeft className="w-5 h-5 text-orange-500" />;
      case 'bank_charge': return <DollarSign className="w-5 h-5 text-red-500" />;
      case 'interest': return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'salary': return <DollarSign className="w-5 h-5 text-purple-500" />;
      case 'commission': return <DollarSign className="w-5 h-5 text-indigo-500" />;
      case 'bill_payment': return <FileText className="w-5 h-5 text-orange-600" />;
      default: return <DollarSign className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTransactionLabel = (type) => {
    const labels = {
      deposit: t('bank.deposit'),
      withdrawal: t('bank.withdrawal'),
      transfer_in: t('bank.transfer_in'),
      transfer_out: t('bank.transfer_out'),
      bank_charge: t('bank.bank_charge'),
      interest: t('bank.interest'),
      salary: t('bank.salary'),
      commission: t('bank.commission'),
      bill_payment: t('bank.bill_payment')
    };
    return labels[type] || type;
  };

  const viewPDF = (url) => {
    if (url) {
      // The URL from API starts with /api/... so we just need the origin
      const fullUrl = url.startsWith('http') ? url : window.location.origin + url;
      window.open(fullUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-blue-600" />{t('bank.bank')}</h1>
          <p className="text-gray-500 mt-1">{t('bank.manage_bank_accounts_and_transactions')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => openModal('account')}
            className="btn-primary flex items-center gap-2"
            data-testid="add-bank-account-btn"
          >
            <Plus className="w-4 h-4" />{t('bank.new_account')}</button>
          <button
            onClick={() => openModal('transaction')}
            className="btn-secondary flex items-center gap-2"
            data-testid="add-transaction-btn"
          >
            <ArrowRightLeft className="w-4 h-4" />{t('bank.transaction')}</button>
          {isAdmin && (
            <button
              onClick={handleRecalculateBalances}
              disabled={recalculating}
              className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50"
              data-testid="recalculate-balances-btn"
            >
              <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? 'جاري الحساب...' : 'إعادة حساب الأرصدة'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={handleHealOrphans}
              disabled={healingOrphans}
              className="flex items-center gap-2 px-4 py-2 bg-fuchsia-100 text-fuchsia-800 rounded-lg hover:bg-fuchsia-200 transition-colors disabled:opacity-50"
              data-testid="heal-orphans-btn"
              title="اربط الحركات اليتيمة (bank_account_id خاطئ) بحسابها البنكي الصحيح"
            >
              <RefreshCw className={`w-4 h-4 ${healingOrphans ? 'animate-spin' : ''}`} />
              {healingOrphans ? 'جاري الفحص...' : '🩺 علاج الحركات اليتيمة'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={handleDedupeTransactions}
              disabled={dedupingTx}
              className="flex items-center gap-2 px-4 py-2 bg-rose-100 text-rose-800 rounded-lg hover:bg-rose-200 transition-colors disabled:opacity-50"
              data-testid="dedupe-transactions-btn"
              title="اكتشف وأزل حركات بنكية مكرَّرة بنفس التاريخ والمبلغ والوصف"
            >
              <RefreshCw className={`w-4 h-4 ${dedupingTx ? 'animate-spin' : ''}`} />
              {dedupingTx ? 'جاري الفحص...' : '🧹 إزالة الحركات المكرَّرة'}
            </button>
          )}
        </div>
      </div>

      {/* Bank Health Drift Detector — Feb 2026 (Wave 1.5).
          Surfaces the three balances + orphan-adjustment count per
          bank in one glanceable card so any drift is caught fast. */}
      {health && health.length > 0 && (
        <div
          className="bg-white rounded-xl border border-slate-200 p-4 space-y-3"
          data-testid="bank-health-panel"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-900">Bank Health (Drift Detector)</h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 hidden md:inline">
                يَفحَص لَحظياً تَطابُق الرصيد المَخزون مع Bank Transactions و GL
              </span>
              {isAdmin && (
                <button
                  onClick={runDiagnoseAndRepair}
                  disabled={diagnosing}
                  data-testid="diagnose-and-repair-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-medium shadow-sm disabled:opacity-50"
                  title="تَشخيص شامِل وإِصلاح تِلقائي لِكُل المُشكِلات"
                >
                  {diagnosing ? '🔄 جارٍ الفَحص...' : '🔧 Diagnose & Repair'}
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {health.map((b) => {
              const tone = {
                green:  'border-green-300 bg-green-50',
                yellow: 'border-yellow-300 bg-yellow-50',
                red:    'border-red-300 bg-red-50',
              }[b.status] || 'border-slate-200 bg-white';
              const dot = {
                green:  'bg-green-500',
                yellow: 'bg-yellow-500',
                red:    'bg-red-500',
              }[b.status] || 'bg-slate-400';
              return (
                <div
                  key={b.bank_account_id}
                  className={`rounded-lg border p-3 ${tone}`}
                  data-testid={`bank-health-card-${b.bank_account_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-sm text-slate-900">{b.bank_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">****{b.account_number_last4}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${dot}`}></span>
                      <span className="text-[11px] uppercase font-bold text-slate-600">{b.status}</span>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Stored</span>
                      <span className="font-mono font-semibold">{b.stored_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Σ Transactions</span>
                      <span className="font-mono">{b.tx_sum?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">GL</span>
                      <span className="font-mono">{b.gl_sum == null ? '—' : b.gl_sum.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    {Math.abs(b.drift_vs_tx) > 0.01 && (
                      <div className="flex justify-between text-red-700 font-semibold">
                        <span>Drift vs Tx</span><span>{b.drift_vs_tx.toFixed(2)}</span>
                      </div>
                    )}
                    {b.orphan_count > 0 && (
                      <div className="text-[11px] text-red-700 space-y-1">
                        <div title={(b.orphan_adjustments || []).join(', ')}>
                          🔴 {b.orphan_count} adjustment(s) بِدون mirror
                        </div>
                        <button
                          type="button"
                          data-testid={`bank-health-backfill-btn-${b.bank_account_id}`}
                          onClick={async () => {
                            if (!window.confirm(`سيُجرى Backfill لِكُل Bank Adjustments الـ orphan في الشَركة (${b.orphan_count} لِهذا البنك). هَل تَريد المُتابَعة؟`)) return;
                            try {
                              const r = await axiosInstance.post('/bank/health/backfill-mirrors');
                              toast.success(`Backfill اكتَمَل: ${r.data?.stats?.mirrored ?? 0} mirror(s) أُنشِأَت.`);
                              fetchData();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'فَشَل الـ Backfill');
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold transition-colors"
                        >
                          <Zap className="w-3 h-3" /> شَغِّل Backfill الآن
                        </button>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-1 pt-1.5 border-t border-slate-200 mt-1.5 text-[10px] text-slate-500">
                      <div title="Transactions count">Tx: {b.transaction_count || 0}</div>
                      <div title="Voided count">Void: {b.voided_count || 0}</div>
                      <div title="Pending adjustments">Pend: {b.pending_adjustments || 0}</div>
                    </div>
                    <div className="text-[10px] text-slate-500 pt-1">
                      Last reconciled: {b.last_reconciliation || '—'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">{t('bank.total_balance')}</p>
                <p className="text-2xl font-bold mt-1">{summary.total_balance?.toLocaleString()} SAR</p>
              </div>
              <Building2 className="w-10 h-10 text-blue-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">{t('bank.monthly_deposits')}</p>
                <p className="text-2xl font-bold mt-1">{summary.monthly_summary?.deposits?.toLocaleString()} SAR</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">{t('bank.monthly_withdrawals')}</p>
                <p className="text-2xl font-bold mt-1">{summary.monthly_summary?.withdrawals?.toLocaleString()} SAR</p>
              </div>
              <TrendingDown className="w-10 h-10 text-red-200" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">{t('bank.accounts')}</p>
                <p className="text-2xl font-bold mt-1">{summary.total_accounts}</p>
              </div>
              <FileText className="w-10 h-10 text-purple-200" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 flex items-center justify-between">
        <nav className="flex gap-4">
          {[
            { id: 'accounts', label: t('bank.accounts'), icon: Building2 },
            { id: 'transactions', label: t('bank.transactions'), icon: ArrowRightLeft },
            { id: 'statements', label: 'كشوف الحسابات', icon: BookOpen },
            { id: 'reconciliation', label: t('bank.reconciliation'), icon: CheckCircle },
            { id: 'engine', label: t('bank.smart_engine'), icon: Sparkles },
            { id: 'orphans', label: 'سجلات يتيمة', icon: AlertTriangle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
        {/* Bank Phase 2 — quick link to the dedicated Bank Adjustments
            screen. Bank Transactions in this page are evidence-only
            (Phase 1); any GL-touching correction (charge / interest /
            FX / opening-balance) must be posted there. */}
        <button
          data-testid="open-bank-adjustments-link"
          onClick={() => navigate('/bank/adjustments')}
          className="mb-2 ml-3 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 font-medium"
        >
          <DollarSign className="w-4 h-4" />
          تَعديلات البنك / Bank Adjustments
        </button>
      </div>

      {/* Accounts Tab */}
      {activeTab === 'accounts' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.code')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.bank')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.account_no')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IBAN</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.balance')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.status')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account, index) => (
                  <tr 
                    key={account.id} 
                    className="hover:bg-gray-50 cursor-pointer" 
                    data-testid={`bank-account-row-${index}`}
                    onClick={() => openDetailModal('account', account)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {account.account_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{account.bank_name}</p>
                        {account.bank_name_arabic && (
                          <p className="text-sm text-gray-500">{account.bank_name_arabic}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.account_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.iban || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-bold ${account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {account.current_balance?.toLocaleString()} {account.currency}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        account.status === 'active' ? 'bg-green-100 text-green-800' :
                        account.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {account.status === 'active' ? t('bank.active') :
                         account.status === 'inactive' ? t('bank.inactive') : t('bank.closed')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedAccount(account);
                            setTransactionForm({ ...transactionForm, bank_account_id: account.id });
                            openModal('transaction');
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title={t('bank.new_transaction')}
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openModal('account', account)}
                          className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                          title={t('bank.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title={t('bank.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {accounts.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">{t('bank.no_bank_accounts_found')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.no')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.date')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.type')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.account')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.description')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.amount')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.balance')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.status')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((trans, index) => (
                  <tr 
                    key={trans.id} 
                    ref={registerRow(trans.id)}
                    className={`hover:bg-gray-50 cursor-pointer ${focusedId === trans.id ? 'focus-highlight' : ''}`}
                    data-testid={`transaction-row-${trans.id}`}
                    onClick={() => openDetailModal('transaction', trans)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {trans.transaction_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {trans.transaction_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(trans.transaction_type)}
                        <span className="text-sm">{getTransactionLabel(trans.transaction_type)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {trans.bank_account_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {trans.description || trans.payee_payer || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-bold ${
                        ['deposit', 'transfer_in', 'interest'].includes(trans.transaction_type)
                          ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {['deposit', 'transfer_in', 'interest'].includes(trans.transaction_type) ? '+' : '-'}
                        {trans.amount?.toLocaleString()} SAR
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                      {trans.balance_after?.toLocaleString()} SAR
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {trans.reconciliation_status === 'matched' ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                          title="مُطابَق مع كَشف الحساب البنكي"
                        >
                          <CheckCircle className="w-3 h-3" /> مُطابَق
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs"
                          title="لم تُطابَق بَعد مع كَشف الحساب البنكي (لا عَلاقة لها بِالاعتِماد المُحاسَبي — القَيد مَوجود في GL)"
                        >
                          <Clock className="w-3 h-3" /> غير مُطابَق
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openDetailModal('transaction', trans)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title={t('bank.view')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => openModal('transaction', trans)}
                              className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                              title={t('bank.edit')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTransaction(trans.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title={t('bank.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">{t('bank.no_transactions_found')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bank Statements Tab — Wave 2A */}
      {activeTab === 'statements' && (
        <BankStatementsTab accounts={accounts} isAdmin={isAdmin} />
      )}

      {/* Orphan Financial Records — Self-Healing wave */}
      {activeTab === 'orphans' && (
        <OrphanReport isAdmin={isAdmin} />
      )}

      {/* Reconciliation Tab */}
      {activeTab === 'reconciliation' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => openModal('reconciliation')}
              className="btn-primary flex items-center gap-2"
              data-testid="start-reconciliation-btn"
            >
              <RefreshCw className="w-4 h-4" />{t('bank.start_reconciliation')}</button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.no')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.account')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.statement_date')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.statement_balance')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.book_balance')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.difference')}</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.status')}</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reconciliations.map((rec, index) => (
                    <tr 
                      key={rec.id} 
                      className="hover:bg-gray-50 cursor-pointer" 
                      data-testid={`reconciliation-row-${index}`}
                      onClick={() => openDetailModal('reconciliation', rec)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rec.reconciliation_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {rec.bank_account_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {rec.statement_date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {rec.statement_balance?.toLocaleString()} SAR
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {rec.book_balance?.toLocaleString()} SAR
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${rec.difference === 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {rec.difference?.toLocaleString()} SAR
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {rec.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                            <CheckCircle className="w-3 h-3" /> مكتمل
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                            <Clock className="w-3 h-3" /> قيد التنفيذ
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openDetailModal('reconciliation', rec)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title={t('bank.view')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => openModal('reconciliation', rec)}
                                className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                                title={t('bank.edit')}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteReconciliation(rec.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title={t('bank.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {reconciliations.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">{t('bank.no_reconciliations_found')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Engine Tab — P4 Bank Realtime Engine */}
      {activeTab === 'engine' && (
        <div className="space-y-4" data-testid="engine-panel">
          {/* Hero / Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-xl p-5 text-white bg-gradient-to-br from-indigo-600 to-violet-600">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">{t('bank.match_rate')}</p>
                  <p className="text-3xl font-bold mt-1" data-testid="engine-rate">
                    {engineSummary ? `${engineSummary.reconciliation_rate}%` : '—'}
                  </p>
                </div>
                <Activity className="w-10 h-10 text-indigo-200" />
              </div>
            </div>
            <div className="rounded-xl p-5 text-white bg-gradient-to-br from-emerald-500 to-emerald-600">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">{t('bank.matched')}</p>
                  <p className="text-3xl font-bold mt-1" data-testid="engine-matched">
                    {engineSummary ? engineSummary.matched : '—'}
                  </p>
                </div>
                <Link2 className="w-10 h-10 text-emerald-200" />
              </div>
            </div>
            <div className="rounded-xl p-5 text-white bg-gradient-to-br from-amber-500 to-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">{t('bank.unmatched')}</p>
                  <p className="text-3xl font-bold mt-1" data-testid="engine-unmatched">
                    {engineSummary ? engineSummary.unmatched : '—'}
                  </p>
                </div>
                <AlertCircle className="w-10 h-10 text-amber-200" />
              </div>
            </div>
            <div className="rounded-xl p-5 text-white bg-gradient-to-br from-slate-700 to-slate-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm">{t('bank.total')}</p>
                  <p className="text-3xl font-bold mt-1" data-testid="engine-total">
                    {engineSummary ? engineSummary.total_transactions : '—'}
                  </p>
                </div>
                <ArrowRightLeft className="w-10 h-10 text-slate-400" />
              </div>
            </div>
          </div>

          {/* ============== P4.1 AI Engine ============== */}
          <div className="bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 rounded-2xl shadow-xl border border-violet-900/40 overflow-hidden">
            <div className="p-5 border-b border-violet-900/40 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{t('bank.ai_scoring_engine')}</h2>
                  <p className="text-xs text-violet-300">Multi-factor confidence scoring · Amount 40% · Date 20% · Reference 25% · Entity 15%</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {['overview', 'review', 'matches'].map(v => (
                  <button
                    key={v}
                    onClick={() => setAiView(v)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      aiView === v
                        ? 'bg-violet-600 text-white shadow-lg shadow-violet-900/40'
                        : 'bg-violet-900/30 text-violet-300 hover:bg-violet-900/50'
                    }`}
                    data-testid={`ai-view-${v}`}
                  >
                    {v === 'overview' ? t('bank.overview') : v === 'review' ? `قائمة المراجعة / Review (${reviewQueue.length})` : t('bank.all')}
                  </button>
                ))}
              </div>
            </div>

            {/* OVERVIEW */}
            {aiView === 'overview' && (
              <div className="p-5 space-y-5" data-testid="ai-overview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-700/10 border border-emerald-500/30 p-4">
                    <p className="text-emerald-300 text-xs font-medium">مطابق تلقائياً ≥85</p>
                    <p className="text-3xl font-bold text-emerald-200 mt-1" data-testid="ai-auto-rate">
                      {aiSummary ? `${aiSummary.auto_rate}%` : '—'}
                    </p>
                    <p className="text-emerald-400/70 text-xs mt-1">{aiSummary?.auto_matched ?? 0} حركة</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-700/10 border border-amber-500/30 p-4">
                    <p className="text-amber-300 text-xs font-medium">يحتاج مراجعة 60-84</p>
                    <p className="text-3xl font-bold text-amber-200 mt-1" data-testid="ai-review-rate">
                      {aiSummary ? `${aiSummary.review_rate}%` : '—'}
                    </p>
                    <p className="text-amber-400/70 text-xs mt-1">{aiSummary?.needs_review ?? 0} حركة</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-rose-500/20 to-rose-700/10 border border-rose-500/30 p-4">
                    <p className="text-rose-300 text-xs font-medium">غير مطابق &lt;60</p>
                    <p className="text-3xl font-bold text-rose-200 mt-1" data-testid="ai-unmatched-rate">
                      {aiSummary ? `${aiSummary.unmatched_rate}%` : '—'}
                    </p>
                    <p className="text-rose-400/70 text-xs mt-1">{aiSummary?.unmatched ?? 0} حركة</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-indigo-500/20 to-indigo-700/10 border border-indigo-500/30 p-4">
                    <p className="text-indigo-300 text-xs font-medium">{t('bank.avg_score')}</p>
                    <p className="text-3xl font-bold text-indigo-200 mt-1" data-testid="ai-avg-score">
                      {aiSummary ? aiSummary.avg_score : '—'}
                    </p>
                    <p className="text-indigo-400/70 text-xs mt-1">من أصل 100</p>
                  </div>
                </div>

                {/* Distribution chart */}
                <div className="rounded-xl bg-slate-900/40 border border-violet-900/30 p-4">
                  <p className="text-sm font-semibold text-violet-200 mb-3">{t('bank.confidence_distribution')}</p>
                  <div className="space-y-2">
                    {[
                      { key: '85-100', label: 'مطابق تلقائياً', color: 'from-emerald-400 to-emerald-600' },
                      { key: '60-85', label: 'مراجعة يدوية', color: 'from-amber-400 to-amber-600' },
                      { key: '30-60', label: 'ضعيف', color: 'from-orange-400 to-rose-600' },
                      { key: '0-30', label: 'لا تطابق', color: 'from-slate-500 to-slate-700' }
                    ].map(row => {
                      const count = aiSummary?.distribution?.[row.key] ?? 0;
                      const total = aiSummary?.total_transactions || 1;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <div key={row.key} className="flex items-center gap-3">
                          <span className="text-xs text-violet-300 w-32 shrink-0">{row.label} ({row.key})</span>
                          <div className="flex-1 h-6 bg-slate-800/60 rounded-md overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${row.color} flex items-center px-2 transition-all`}
                                 style={{ width: `${Math.max(pct, count > 0 ? 5 : 0)}%` }}>
                              <span className="text-xs font-semibold text-white">{count}</span>
                            </div>
                          </div>
                          <span className="text-xs text-violet-200 w-12 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* REVIEW QUEUE */}
            {aiView === 'review' && (
              <div className="p-5" data-testid="ai-review-panel">
                {reviewQueue.length === 0 ? (
                  <div className="text-center py-12 text-violet-300">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                    <p className="font-semibold">{t('bank.review_queue_empty')}</p>
                    <p className="text-xs text-violet-400 mt-1">كل المطابقات إما تلقائية أو غير قابلة للمطابقة</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviewQueue.map(item => (
                      <div key={item.id} className="rounded-xl bg-slate-900/40 border border-amber-500/30 p-4">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 rounded-md bg-amber-500/20 text-amber-300 text-xs font-bold">
                                Score: {item.score}/100
                              </span>
                              <span className="px-2 py-0.5 rounded-md bg-violet-500/20 text-violet-300 text-xs font-medium">
                                {item.matched_type}
                              </span>
                            </div>
                            <p className="text-sm text-violet-100 font-mono">{item.transaction_no}</p>
                            <p className="text-xs text-violet-400">
                              {item.transaction?.description || '—'} · {item.transaction?.transaction_date}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-white">{Number(item.amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</p>
                            {item.difference !== 0 && (
                              <p className="text-xs text-amber-300">Δ {item.difference}</p>
                            )}
                          </div>
                        </div>

                        {/* Breakdown bars */}
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {Object.entries(item.breakdown || {}).map(([k, v]) => {
                            const max = { amount: 40, date: 20, reference: 25, entity: 15 }[k] || 25;
                            const pct = (v / max) * 100;
                            return (
                              <div key={k} className="bg-slate-800/60 rounded-md p-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] uppercase text-violet-400">{k}</span>
                                  <span className="text-xs font-semibold text-violet-100">{v}/{max}</span>
                                </div>
                                <div className="h-1.5 bg-slate-900 rounded overflow-hidden">
                                  <div className="h-full bg-gradient-to-r from-violet-400 to-indigo-500" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Candidate context */}
                        {item.candidate && (
                          <div className="rounded-lg bg-slate-800/40 px-3 py-2 mb-3 text-xs text-violet-200 border border-violet-900/30">
                            <span className="text-violet-400">Candidate:</span> {item.matched_ref || item.candidate.id}
                            {item.candidate.amount && <> · المبلغ {Number(item.candidate.amount).toLocaleString('en', { minimumFractionDigits: 2 })}</>}
                            {item.candidate.customer_name && <> · {item.candidate.customer_name}</>}
                            {item.candidate.supplier_name && <> · {item.candidate.supplier_name}</>}
                          </div>
                        )}

                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => handleRejectReview(item.transaction_id)}
                            className="px-3 py-1.5 text-xs rounded-lg bg-rose-600/20 text-rose-300 hover:bg-rose-600/30 border border-rose-500/30"
                            data-testid={`ai-reject-${item.transaction_id}`}
                          >{t('bank.reject')}</button>
                          <button
                            onClick={() => handleConfirmReview(item.transaction_id)}
                            className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:opacity-90"
                            data-testid={`ai-confirm-${item.transaction_id}`}
                          >{t('bank.confirm')}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ALL MATCHES */}
            {aiView === 'matches' && (
              <div className="p-5" data-testid="ai-matches-panel">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="text-left text-xs text-violet-400 border-b border-violet-900/30">
                        <th className="py-2 px-3">TX</th>
                        <th className="py-2 px-3 text-right">Amount</th>
                        <th className="py-2 px-3">Match</th>
                        <th className="py-2 px-3 text-center">Score</th>
                        <th className="py-2 px-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiMatches.map(m => (
                        <tr key={m.id} className="border-b border-violet-900/20 text-violet-100 hover:bg-violet-900/20">
                          <td className="py-2 px-3 font-mono text-xs">{m.transaction_no}</td>
                          <td className="py-2 px-3 text-right text-sm">{Number(m.amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                          <td className="py-2 px-3 text-xs">{m.matched_type ? `${m.matched_type} · ${m.matched_ref || '—'}` : '—'}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                              m.score >= 85 ? 'bg-emerald-500/20 text-emerald-300' :
                              m.score >= 60 ? 'bg-amber-500/20 text-amber-300' :
                              'bg-slate-700 text-slate-300'
                            }`}>{m.score}</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`text-xs ${
                              m.status === 'auto_reconciled' ? 'text-emerald-400' :
                              m.status === 'needs_review' ? 'text-amber-400' :
                              'text-slate-400'
                            }`}>
                              {m.status === 'auto_reconciled' ? '✓ مطابق' :
                               m.status === 'needs_review' ? '⏳ مراجعة' :
                               '✗ غير مطابق'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {aiMatches.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-12 text-center text-violet-400">{t('bank.no_ai_scoring_data_yet')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>



          {/* Action bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <span>محرك المطابقة الذكي يربط كل معاملة بنكية بفاتورة/سداد/مصروف تلقائياً.</span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={engineFilter}
                onChange={(e) => setEngineFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500"
                data-testid="engine-filter"
              >
                <option value="all">{t('bank.all')}</option>
                <option value="reconciled">{t('bank.matched')}</option>
                <option value="unmatched">{t('bank.unmatched')}</option>
              </select>
              <button
                onClick={fetchEngineData}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
                data-testid="engine-refresh"
              >
                <RefreshCw className="w-4 h-4" />{t('bank.refresh')}</button>
              {isAdmin && (
                <button
                  onClick={handleRerunEngine}
                  disabled={engineRunning}
                  className="px-4 py-2 text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2 font-medium"
                  data-testid="engine-rerun"
                >
                  <Zap className="w-4 h-4" />
                  {engineRunning ? t('bank.running') : t('bank.run_engine')}
                </button>
              )}
            </div>
          </div>

          {/* Links table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{t('bank.reconciliation_links')}</h3>
              <span className="text-xs text-gray-500">{engineLinks.length} إدخال / entries</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.tx')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('bank.amount')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.match')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.ref')}</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.confidence')}</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.status')}</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.action')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {engineLinks
                    .filter(l => engineFilter === 'all' || l.status === engineFilter)
                    .map(link => (
                      <tr key={link.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-mono text-gray-900">{link.transaction_no || (link.transaction_id || '').slice(0, 8)}</td>
                        <td className="px-4 py-2 text-right text-sm font-semibold">{Number(link.amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-2 text-sm">
                          {link.matched_type ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-violet-100 text-violet-700 font-medium">
                              {link.matched_type}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{link.matched_ref || '—'}</td>
                        <td className="px-4 py-2 text-center text-sm">
                          {link.confidence > 0 ? (
                            <span className={`text-xs font-medium ${link.confidence >= 0.9 ? 'text-emerald-600' : link.confidence >= 0.75 ? 'text-amber-600' : 'text-gray-500'}`}>
                              {Math.round(link.confidence * 100)}%
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {link.status === 'reconciled' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 font-medium">
                              <CheckCircle className="w-3 h-3" /> مطابق
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
                              <AlertCircle className="w-3 h-3" /> غير مطابق
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {link.status === 'reconciled' && isAdmin && (
                            <button
                              onClick={() => handleUnlink(link.transaction_id)}
                              className="text-xs text-red-600 hover:text-red-800"
                              data-testid={`unlink-${link.transaction_id}`}
                            >{t('bank.unlink')}</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  {engineLinks.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-gray-500">{t('bank.no_links_yet_run_the_engine')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rules */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{t('bank.smart_rules')}</h3>
              <button
                onClick={() => openRuleModal()}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                data-testid="add-rule-btn"
              >
                <Plus className="w-4 h-4" />{t('bank.add_rule')}</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.rule')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.keywords')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bank.account')}</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('bank.conf')}</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">إجراء</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {bankRules.map(rule => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-semibold text-gray-900">{rule.rule_name}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {(rule.keywords || []).map((k, i) => (
                          <span key={i} className="inline-block px-2 py-0.5 mr-1 mb-1 text-xs bg-violet-50 text-violet-700 rounded">
                            {k}
                          </span>
                        ))}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{rule.account_name || '—'}</td>
                      <td className="px-4 py-2 text-center text-sm">{Math.round((rule.confidence_threshold || 0) * 100)}%</td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openRuleModal(rule)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                            data-testid={`edit-rule-${rule.id}`}
                          >
                            <Edit2 className="w-4 h-4 inline" />
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="text-xs text-red-600 hover:text-red-800"
                            data-testid={`delete-rule-${rule.id}`}
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {bankRules.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-12 text-center text-gray-500">{t('bank.no_smart_rules_yet')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">
                {selectedRule ? t('bank.edit_rule') : t('bank.new_smart_rule')}
              </h2>
              <button onClick={() => setShowRuleModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveRule} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.rule_name')}</label>
                <input
                  type="text"
                  value={ruleForm.rule_name}
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="rule-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.keywords_comma_separated')}</label>
                <input
                  type="text"
                  value={ruleForm.keywords}
                  onChange={(e) => setRuleForm({ ...ruleForm, keywords: e.target.value })}
                  className="form-input"
                  required
                  placeholder="VAT, ZATCA, ضريبة"
                  data-testid="rule-keywords-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.default_account')}</label>
                <input
                  type="text"
                  value={ruleForm.account_name}
                  onChange={(e) => setRuleForm({ ...ruleForm, account_name: e.target.value })}
                  className="form-input"
                  placeholder="e.g. VAT Output"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.confidence_threshold_0_1')}</label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={ruleForm.confidence_threshold}
                  onChange={(e) => setRuleForm({ ...ruleForm, confidence_threshold: e.target.value })}
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.notes')}</label>
                <textarea
                  value={ruleForm.notes}
                  onChange={(e) => setRuleForm({ ...ruleForm, notes: e.target.value })}
                  className="form-input"
                  rows="2"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowRuleModal(false)} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">{t('bank.cancel')}</button>
                <button type="submit" className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-testid="save-rule-btn">{t('bank.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">
                {modalType === 'account' && (selectedAccount ? t('bank.edit_account') : t('bank.new_bank_account'))}
                {modalType === 'transaction' && (selectedTransaction ? t('bank.edit_transaction') : t('bank.new_transaction'))}
                {modalType === 'reconciliation' && (selectedReconciliation ? t('bank.edit_reconciliation') : t('bank.new_reconciliation'))}
              </h2>
              <button onClick={() => { setShowModal(false); setSelectedTransaction(null); setSelectedReconciliation(null); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={
              modalType === 'account' ? handleCreateAccount :
              modalType === 'transaction' ? handleCreateTransaction :
              handleCreateReconciliation
            } className="p-4 space-y-4">
              
              {modalType === 'account' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">اسم البنك (إنجليزي) *</label>
                      <input
                        type="text"
                        value={accountForm.bank_name}
                        onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })}
                        className="form-input"
                        required
                        placeholder="e.g. Al Rajhi Bank"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">اسم البنك (عربي)</label>
                      <input
                        type="text"
                        value={accountForm.bank_name_arabic}
                        onChange={(e) => setAccountForm({ ...accountForm, bank_name_arabic: e.target.value })}
                        className="form-input"
                        placeholder="مثال: بنك الراجحي"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">اسم الحساب *</label>
                    <input
                      type="text"
                      value={accountForm.account_name}
                      onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                      className="form-input"
                      required
                      placeholder="e.g. Main Operating Account"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">رقم الحساب *</label>
                      <input
                        type="text"
                        value={accountForm.account_number}
                        onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })}
                        className="form-input"
                        required
                        placeholder="e.g. 1234567890"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                      <input
                        type="text"
                        value={accountForm.iban}
                        onChange={(e) => setAccountForm({ ...accountForm, iban: e.target.value })}
                        className="form-input"
                        placeholder="SA..."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">رمز SWIFT</label>
                      <input
                        type="text"
                        value={accountForm.swift_code}
                        onChange={(e) => setAccountForm({ ...accountForm, swift_code: e.target.value })}
                        className="form-input"
                        placeholder="e.g. RJHISARI"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.branch')}</label>
                      <input
                        type="text"
                        value={accountForm.branch}
                        onChange={(e) => setAccountForm({ ...accountForm, branch: e.target.value })}
                        className="form-input"
                        placeholder="e.g. Riyadh Main Branch"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.currency')}</label>
                      <select
                        value={accountForm.currency}
                        onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value })}
                        className="form-select"
                      >
                        <option value="SAR">SAR - ريال سعودي</option>
                        <option value="USD">USD - دولار</option>
                        <option value="EUR">EUR - يورو</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">الرصيد الافتتاحي</label>
                      <input
                        type="number"
                        step="0.01"
                        value={accountForm.opening_balance}
                        onChange={(e) => setAccountForm({ ...accountForm, opening_balance: parseFloat(e.target.value) || 0 })}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.account_notes')}</label>
                    <textarea
                      value={accountForm.notes}
                      onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })}
                      className="form-textarea border-2 border-gray-200 focus:border-blue-500"
                      rows="2"
                      placeholder={t('bank.enter_any_additional_notes_about_this_ba')}
                    />
                  </div>
                  {/* PDF Attachment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Paperclip className="w-4 h-4 inline mr-1" />{t('bank.attach_pdf_file')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.gif"
                        onChange={(e) => handleFileUpload(e, 'account')}
                        className="hidden"
                        id="account-file-upload"
                      />
                      <label
                        htmlFor="account-file-upload"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        {uploading ? 'جاري الرفع...' : t('bank.choose_file')}
                      </label>
                      {accountForm.attachment_url && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> تم الرفع
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {modalType === 'transaction' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الحساب البنكي *</label>
                    <select
                      value={transactionForm.bank_account_id}
                      onChange={(e) => setTransactionForm({ ...transactionForm, bank_account_id: e.target.value })}
                      className="form-select"
                      required
                    >
                      <option value="">اختر الحساب...</option>
                      {accounts.filter(a => a.status === 'active').map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {acc.bank_name} - {acc.account_number} ({acc.current_balance?.toLocaleString()} {acc.currency})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">نوع المعاملة *</label>
                      <select
                        value={transactionForm.transaction_type}
                        onChange={(e) => setTransactionForm({ ...transactionForm, transaction_type: e.target.value })}
                        className="form-select"
                        required
                      >
                        <option value="deposit">{t('bank.deposit')}</option>
                        <option value="withdrawal">{t('bank.withdrawal')}</option>
                        <option value="transfer_out">{t('bank.transfer')}</option>
                        <option value="salary">{t('bank.salary')}</option>
                        <option value="commission">{t('bank.commission')}</option>
                        <option value="bill_payment">{t('bank.bill_payment')}</option>
                        <option value="bank_charge">{t('bank.bank_charge')}</option>
                        <option value="interest">{t('bank.interest')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">التاريخ *</label>
                      <input
                        type="date"
                        value={transactionForm.transaction_date}
                        onChange={(e) => setTransactionForm({ ...transactionForm, transaction_date: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                  {transactionForm.transaction_type === 'transfer_out' && (
                    <>
                      <div
                        data-testid="transfer-supplier-warning-banner"
                        className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm"
                      >
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold text-blue-900 mb-1">
                              تَحويل بنكي داخِلي فَقَط
                            </p>
                            <p className="text-blue-800 leading-relaxed">
                              التحويل البنكي مُخَصَّص فَقَط بين حسابات شَركتك البنكية.
                              لِدَفع مورِد خارِجي ثَمَن مَواد أو خَدَمات، استَخدِم Supplier Payment.
                            </p>
                            <button
                              type="button"
                              data-testid="transfer-pay-supplier-cta"
                              onClick={() => {
                                setShowModal(false);
                                const params = new URLSearchParams({ type: 'made' });
                                const srcBank = accounts.find(
                                  (a) => a.id === transactionForm.bank_account_id
                                );
                                if (srcBank?.linked_account_id) {
                                  params.set('account_id', srcBank.linked_account_id);
                                }
                                navigate(`/payments?${params.toString()}`);
                              }}
                              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
                            >
                              <ArrowUpCircle className="w-3.5 h-3.5" />
                              الانتِقال إلى Pay Supplier
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">تحويل إلى حساب *</label>
                        <select
                          value={transactionForm.transfer_to_account_id}
                          onChange={(e) => setTransactionForm({ ...transactionForm, transfer_to_account_id: e.target.value })}
                          className="form-select"
                          required
                          data-testid="transfer-to-account-select"
                        >
                          <option value="">اختر الحساب...</option>
                          {accounts.filter(a => a.status === 'active' && a.id !== transactionForm.bank_account_id).map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.bank_name} - {acc.account_number}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">المبلغ *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={transactionForm.amount}
                        onChange={(e) => setTransactionForm({ ...transactionForm, amount: parseFloat(e.target.value) || 0 })}
                        className="form-input"
                        required
                        min="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.reference')}</label>
                      <input
                        type="text"
                        value={transactionForm.reference}
                        onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })}
                        className="form-input"
                        placeholder="e.g. CHQ-001"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الدافع/المستلم</label>
                    <input
                      type="text"
                      value={transactionForm.payee_payer}
                      onChange={(e) => setTransactionForm({ ...transactionForm, payee_payer: e.target.value })}
                      className="form-input"
                      placeholder="e.g. Customer Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.transaction_description')}</label>
                    <textarea
                      value={transactionForm.description}
                      onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                      className="form-textarea border-2 border-gray-200 focus:border-blue-500"
                      rows="2"
                      placeholder={t('bank.enter_detailed_description_of_the_transa')}
                    />
                  </div>
                  {/* PDF Attachment for Transaction */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Paperclip className="w-4 h-4 inline mr-1" />{t('bank.attach_pdf_file')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.gif"
                        onChange={(e) => handleFileUpload(e, 'transaction')}
                        className="hidden"
                        id="transaction-file-upload"
                      />
                      <label
                        htmlFor="transaction-file-upload"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        {uploading ? 'جاري الرفع...' : t('bank.choose_file')}
                      </label>
                      {transactionForm.attachment_url && (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> تم الرفع
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {modalType === 'reconciliation' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الحساب البنكي *</label>
                    <select
                      value={reconciliationForm.bank_account_id}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, bank_account_id: e.target.value })}
                      className="form-select"
                      required
                    >
                      <option value="">اختر الحساب...</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {acc.bank_name} - {acc.account_number}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ كشف الحساب *</label>
                      <input
                        type="date"
                        value={reconciliationForm.statement_date}
                        onChange={(e) => setReconciliationForm({ ...reconciliationForm, statement_date: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">رصيد الكشف *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={reconciliationForm.statement_balance}
                        onChange={(e) => setReconciliationForm({ ...reconciliationForm, statement_balance: parseFloat(e.target.value) || 0 })}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('bank.notes')}</label>
                    <textarea
                      value={reconciliationForm.notes}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, notes: e.target.value })}
                      className="form-textarea"
                      rows="2"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">{t('bank.cancel')}</button>
                <button type="submit" className="btn-primary">
                  {modalType === 'account' ? (selectedAccount ? t('bank.update') : t('bank.create')) :
                   modalType === 'transaction' ? t('bank.record') : t('bank.start')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal - Shows all data when clicking on a row */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-xl">
              <h2 className="text-lg font-bold">
                {detailType === 'account' && t('bank.bank_account_details')}
                {detailType === 'transaction' && t('bank.transaction_details')}
                {detailType === 'reconciliation' && t('bank.reconciliation_details')}
              </h2>
              <button onClick={() => setShowDetailModal(false)} className="p-1 hover:bg-white/20 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Account Details */}
              {detailType === 'account' && selectedAccount && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.account_code')}</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.account_code}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.status')}</p>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        selectedAccount.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedAccount.status === 'active' ? t('bank.active') : 'غير نشط'}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">اسم البنك (EN)</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.bank_name}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">اسم البنك (AR)</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.bank_name_arabic || '-'}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">{t('bank.account_name')}</p>
                    <p className="font-semibold text-gray-900">{selectedAccount.account_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.account_number')}</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.account_number}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">IBAN</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.iban || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">رمز SWIFT</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.swift_code || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.branch')}</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.branch || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.currency')}</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.currency}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">الرصيد الافتتاحي</p>
                      <p className="font-semibold text-gray-900">{selectedAccount.opening_balance?.toLocaleString()} {selectedAccount.currency}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-blue-600">الرصيد الحالي</p>
                      <p className="font-bold text-blue-700 text-lg">{selectedAccount.current_balance?.toLocaleString()} {selectedAccount.currency}</p>
                    </div>
                  </div>
                  {selectedAccount.notes && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.notes')}</p>
                      <p className="text-gray-900">{selectedAccount.notes}</p>
                    </div>
                  )}
                  {/* View PDF Button */}
                  {selectedAccount.attachment_url && (
                    <div className="pt-4 border-t">
                      <button
                        onClick={() => viewPDF(selectedAccount.attachment_url)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        data-testid="view-pdf-btn"
                      >
                        <FileText className="w-5 h-5" />{t('bank.view_pdf')}</button>
                    </div>
                  )}
                </div>
              )}

              {/* Transaction Details */}
              {detailType === 'transaction' && selectedTransaction && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.transaction_no')}</p>
                      <p className="font-semibold text-gray-900">{selectedTransaction.transaction_no}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.date')}</p>
                      <p className="font-semibold text-gray-900">{selectedTransaction.transaction_date}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.type')}</p>
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(selectedTransaction.transaction_type)}
                        <span className="font-semibold">{getTransactionLabel(selectedTransaction.transaction_type)}</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">الحساب البنكي</p>
                      <p className="font-semibold text-gray-900">{selectedTransaction.bank_account_name}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${['deposit', 'transfer_in', 'interest'].includes(selectedTransaction.transaction_type) ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className="text-xs text-gray-500">{t('bank.amount')}</p>
                      <p className={`font-bold text-xl ${['deposit', 'transfer_in', 'interest'].includes(selectedTransaction.transaction_type) ? 'text-green-600' : 'text-red-600'}`}>
                        {['deposit', 'transfer_in', 'interest'].includes(selectedTransaction.transaction_type) ? '+' : '-'}
                        {selectedTransaction.amount?.toLocaleString()} SAR
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-blue-600">{t('bank.balance_after')}</p>
                      <p className="font-bold text-xl text-blue-700">{selectedTransaction.balance_after?.toLocaleString()} SAR</p>
                    </div>
                  </div>
                  {selectedTransaction.reference && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.reference')}</p>
                      <p className="font-semibold text-gray-900">{selectedTransaction.reference}</p>
                    </div>
                  )}
                  {selectedTransaction.payee_payer && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.payee_payer')}</p>
                      <p className="font-semibold text-gray-900">{selectedTransaction.payee_payer}</p>
                    </div>
                  )}
                  {selectedTransaction.description && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.description')}</p>
                      <p className="text-gray-900">{selectedTransaction.description}</p>
                    </div>
                  )}
                  {selectedTransaction.transfer_to_account_name && (
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <p className="text-xs text-orange-600">{t('bank.transfer_to')}</p>
                      <p className="font-semibold text-orange-700">{selectedTransaction.transfer_to_account_name}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">{t('bank.reconciliation_status')}</p>
                    {selectedTransaction.reconciliation_status === 'matched' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        <CheckCircle className="w-4 h-4" />{t('bank.matched')}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                        <Clock className="w-4 h-4" />{t('bank.pending')}</span>
                    )}
                  </div>

                  {/* Phase 7.x · Bank → Journal Navigation Layer.
                      Every bank transaction creates a journal entry via
                      `_create_bank_journal_entry`. The journal_id is
                      stored on the transaction document but was never
                      surfaced to the user. Click → focus highlight on
                      the Journal page row. */}
                  {selectedTransaction.journal_id && (
                    <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-indigo-600" />
                        <div>
                          <p className="text-xs text-indigo-600 font-medium">القيد المحاسبي المرتبط</p>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">
                            {selectedTransaction.journal_id.slice(0, 18)}…
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="open-linked-journal-btn"
                        onClick={() => {
                          setShowDetailModal(false);
                          navigate(`/journal?focus=${selectedTransaction.journal_id}`);
                        }}
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors flex items-center gap-1"
                      >
                        <BookOpen className="w-3.5 h-3.5" />
                        افتح القيد
                      </button>
                    </div>
                  )}

                  {/* Phase 7.x · Fix #3 — Explainable Matching Engine.
                      When the tx has a persisted Reason Object (AI-
                      matched transactions), surface the full narrative
                      so the operator can see WHY the system chose
                      this candidate. Hidden when there's no match. */}
                  {matchReason && (
                    <MatchReasonCard reason={matchReason} />
                  )}
                  {/* View PDF Button */}
                  {selectedTransaction.attachment_url && (
                    <div className="pt-4 border-t">
                      <button
                        onClick={() => viewPDF(selectedTransaction.attachment_url)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        data-testid="view-pdf-btn"
                      >
                        <FileText className="w-5 h-5" />{t('bank.view_pdf')}</button>
                    </div>
                  )}
                </div>
              )}

              {/* Reconciliation Details */}
              {detailType === 'reconciliation' && selectedReconciliation && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.reconciliation_no')}</p>
                      <p className="font-semibold text-gray-900">{selectedReconciliation.reconciliation_no}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.status')}</p>
                      {selectedReconciliation.status === 'completed' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                          <CheckCircle className="w-4 h-4" />{t('bank.completed')}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                          <Clock className="w-4 h-4" />{t('bank.in_progress')}</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">{t('bank.bank_account')}</p>
                    <p className="font-semibold text-gray-900">{selectedReconciliation.bank_account_name}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">{t('bank.statement_date')}</p>
                    <p className="font-semibold text-gray-900">{selectedReconciliation.statement_date}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-blue-600">{t('bank.statement_balance')}</p>
                      <p className="font-bold text-lg text-blue-700">{selectedReconciliation.statement_balance?.toLocaleString()} SAR</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-xs text-purple-600">{t('bank.book_balance')}</p>
                      <p className="font-bold text-lg text-purple-700">{selectedReconciliation.book_balance?.toLocaleString()} SAR</p>
                    </div>
                    <div className={`p-3 rounded-lg ${selectedReconciliation.difference === 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className={`text-xs ${selectedReconciliation.difference === 0 ? 'text-green-600' : 'text-red-600'}`}>{t('bank.difference')}</p>
                      <p className={`font-bold text-lg ${selectedReconciliation.difference === 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {selectedReconciliation.difference?.toLocaleString()} SAR
                      </p>
                    </div>
                  </div>
                  {selectedReconciliation.notes && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t('bank.notes')}</p>
                      <p className="text-gray-900">{selectedReconciliation.notes}</p>
                    </div>
                  )}

                  {/* Phase 7.x · Fix #2 — Reconciliation Versioning panel.
                      Every match attempt and adjustment is now append-only.
                      The legacy in-place PUT endpoints are still there for
                      backwards compatibility, but new flows go through the
                      versioned amend → post pipeline. */}
                  <div className="pt-2 border-t border-slate-200">
                    <ReconciliationVersionsPanel
                      reconciliation={selectedReconciliation}
                      onUpdated={(updated) => {
                        if (updated) setSelectedReconciliation(updated);
                        fetchData();
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >{t('bank.close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Diagnose & Repair result modal — populated after a successful
          POST /bank/health/diagnose-and-repair so the operator can see
          exactly what was healed and what (if anything) still needs a
          manual fix. */}
      {diagnoseReport && (
        <DiagnoseRepairResultModal
          report={diagnoseReport}
          onClose={() => setDiagnoseReport(null)}
        />
      )}
    </div>
  );
};

export default Bank;
